"use client"

import { useRef, useState } from "react"
import { Check, Loader2, Plus } from "lucide-react"

import { Field, FieldShell, FIELD_UNDERLINE, Segmented } from "@/components/graphite/field"
import { OverlayNav, OverlayNavAction } from "@/components/graphite/overlay-nav"
import { ShowMore } from "@/components/graphite/show-more"
import { SideRail } from "@/components/graphite/side-rail"
import { TextAction } from "@/components/graphite/text-action"
import { useVisibleBatch } from "@/hooks/use-visible-batch"
import { cn } from "@/lib/utils"
import type { TaskRow, TaskScope } from "@/hooks/use-candidate-tasks"
import type { TaskPriority } from "openapi/api/model"

/**
 * Tasks on the candidate record, from canvas artboards 14c (wide) and 14p
 * (narrow), with 14j/14s as the light pair.
 *
 * Two screens rather than one, on turn 20's rule for the rail: "Counts are the
 * trigger. A row with a number opens the list; the action beside it opens the
 * composer." Comments already work that way; tasks did not, and the difference
 * showed. 14c docks the composer along the foot of the list as a single line of
 * fields, which reads well in an artboard and badly in use: writing a task was
 * a strip a couple of centimetres tall under a screenful of tasks already
 * written, with placeholders where the labels should be. Asking for a task now
 * gives the task the surface, and the ones already open move to the rail where
 * the width allows.
 *
 * Purely presentational — rows, counts and handlers all arrive as props from
 * `useCandidateTasks`.
 */

const SECTION_LABEL =
  "text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground"
const META = "font-mono text-[11px]"

/** Which of the two screens the tab is showing. */
export type TaskMode = "list" | "new"

function Checkbox({
  checked,
  onToggle,
  disabled,
  label,
}: {
  checked: boolean
  onToggle: () => void
  disabled?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-label={label}
      aria-pressed={checked}
      className="mt-0.5 flex w-[13px] flex-none items-center text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 @3xl:mt-0"
    >
      {checked ? (
        <Check className="h-3.5 w-3.5 text-brand-ink" />
      ) : (
        <span className="block h-[9px] w-[9px] rounded-[2px] border transition-colors hover:border-primary @3xl:h-[9px] @3xl:w-[9px]" />
      )}
    </button>
  )
}

function OpenRow({
  task,
  onToggle,
  compact,
}: {
  task: TaskRow
  onToggle: (task: TaskRow) => void
  /** The rail draws the same rows in a third of the width, so they stack. */
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 border-t py-[13px]",
        !compact && "@3xl:items-center",
      )}
    >
      <Checkbox
        checked={false}
        onToggle={() => onToggle(task)}
        disabled={!task.persistedId}
        label={`Mark "${task.title}" done`}
      />

      {/* Narrow stacks the meta under the title; wide keeps one table row. */}
      <div className={cn("min-w-0 flex-1", !compact && "@3xl:contents")}>
        <div
          className={cn(
            "truncate text-[13.5px] leading-[1.45]",
            compact
              ? "text-[12.5px]"
              : "@3xl:min-w-0 @3xl:flex-1 @3xl:text-[12.5px] @3xl:leading-[1.4]",
          )}
        >
          {task.title}
        </div>
        <div
          className={cn(
            "mt-1.5 flex items-baseline gap-3",
            !compact && "@3xl:mt-0 @3xl:contents",
          )}
        >
          <span
            className={cn(
              META,
              task.isLate ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {task.due}
          </span>
          <span
            className={cn(
              META,
              "text-muted-foreground",
              !compact && "@3xl:w-[92px] @3xl:flex-none @3xl:text-right",
            )}
          >
            {task.status}
          </span>
        </div>
      </div>
    </div>
  )
}

function ClosedRow({ task }: { task: TaskRow }) {
  return (
    <div className="flex items-center gap-3 border-t py-[13px]">
      <span className="flex w-[13px] flex-none items-center text-brand-ink">
        <Check className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[1.4] text-muted-foreground">
        {task.title}
      </span>
      <span className={cn(META, "text-muted-foreground")}>{task.closedOn}</span>
      <span
        className={cn(META, "hidden w-[92px] flex-none text-right text-muted-foreground @3xl:inline")}
      >
        done
      </span>
    </div>
  )
}

const PRIORITIES: TaskPriority[] = ["low", "medium", "high"]

/** The job/all narrowing, above the list and again in the rail. */
function ScopeFilter({
  scope,
  onScopeChange,
  className,
}: {
  scope: TaskScope
  onScopeChange: (scope: TaskScope) => void
  className?: string
}) {
  return (
    <div className={cn("flex flex-none items-center gap-3.5", className)}>
      {(["job", "all"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onScopeChange(option)}
          className={cn(
            "border-b pb-[3px] text-[11px] transition-colors",
            scope === option
              ? "border-primary font-medium text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {option === "job" ? "This job" : "All"}
        </button>
      ))}
    </div>
  )
}

/**
 * The open tasks, whatever state the fetch is in.
 *
 * Loading, failed, empty and populated are one chain rather than four
 * independent conditions, so an empty list can never be drawn while the fetch
 * is still running — "Nothing open" reads as a fact about the record.
 */
function OpenTasks({
  open,
  onToggle,
  isLoading,
  isError,
  compact,
  emptyAction,
  /** What re-windows the list — the scope, here. */
  resetKey,
}: {
  open: TaskRow[]
  onToggle: (task: TaskRow) => void
  isLoading: boolean
  isError: boolean
  compact?: boolean
  emptyAction?: React.ReactNode
  resetKey?: unknown
}) {
  const { visible, remaining, showMore } = useVisibleBatch(open, resetKey)

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 border-t py-[13px] text-[12.5px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading tasks…
      </p>
    )
  }

  if (isError) {
    return (
      <p className="border-t py-[13px] text-[12.5px] text-destructive">
        Tasks could not be loaded.
      </p>
    )
  }

  if (open.length === 0) {
    return (
      <div className="border-t py-[13px]">
        <p className="text-[12.5px] text-muted-foreground">
          Nothing open on this record.
        </p>
        {emptyAction}
      </div>
    )
  }

  return (
    <>
      {visible.map((task) => (
        <OpenRow
          key={task.id}
          task={task}
          onToggle={onToggle}
          compact={compact}
        />
      ))}
      <ShowMore remaining={remaining} onShowMore={showMore} noun="tasks" />
    </>
  )
}

/** The finished ones, windowed the same way — a year's worth is a lot of rows. */
function ClosedTasks({
  closed,
  resetKey,
}: {
  closed: TaskRow[]
  resetKey?: unknown
}) {
  const { visible, remaining, showMore } = useVisibleBatch(closed, resetKey)
  if (closed.length === 0) return null

  return (
    <div className="mt-6">
      <div className={SECTION_LABEL}>Closed</div>
      <div className="mt-2">
        {visible.map((task) => (
          <ClosedRow key={task.id} task={task} />
        ))}
        <ShowMore remaining={remaining} onShowMore={showMore} noun="closed tasks" />
      </div>
    </div>
  )
}

/**
 * Every task on the record, which is what the Tasks count opens.
 *
 * The composer is not drawn here. Writing one is a screen of its own, reached
 * from "New" — the same trip the Comments count makes.
 */
function TaskList({
  open,
  closed,
  openCount,
  lateCount,
  scope,
  onScopeChange,
  canScopeToJob,
  onToggle,
  onNew,
  onBack,
  isLoading,
  isError,
}: {
  open: TaskRow[]
  closed: TaskRow[]
  openCount: number
  lateCount: number
  scope: TaskScope
  onScopeChange: (scope: TaskScope) => void
  canScopeToJob: boolean
  onToggle: (task: TaskRow) => void
  onNew: () => void
  onBack: () => void
  isLoading: boolean
  isError: boolean
}) {
  return (
    <div className="flex h-full min-h-0 flex-col px-4 py-4 @3xl:px-[26px] @3xl:py-5">
      {/*
        Back sits at every width, not only on narrow: the record hides its
        header during a takeover below 768px, and a 1440 window with Robin open
        leaves the record 765.
      */}
      <OverlayNav
        onBack={onBack}
        backLabel="Back to the record"
        title="Tasks"
        meta={
          <>
            <span className={cn(META, "text-muted-foreground")}>
              {openCount} open
            </span>
            {lateCount > 0 && (
              <span className={cn(META, "text-destructive")}>{lateCount} late</span>
            )}
          </>
        }
        actions={
          <OverlayNavAction tone="teal" onClick={onNew}>
            New
          </OverlayNavAction>
        }
      />

      {canScopeToJob && (
        <ScopeFilter scope={scope} onScopeChange={onScopeChange} className="mb-1" />
      )}

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        <OpenTasks
          open={open}
          onToggle={onToggle}
          isLoading={isLoading}
          isError={isError}
          resetKey={scope}
          emptyAction={
            <TextAction tone="teal" onClick={onNew} className="mt-2.5 text-[12.5px]">
              <Plus className="h-3.5 w-3.5" />
              Write the first one
            </TextAction>
          }
        />

        <ClosedTasks closed={closed} resetKey={scope} />
      </div>
    </div>
  )
}

/**
 * Writing a task, with the surface to do it in.
 *
 * Every field carries its label, because a task is four separate decisions —
 * what, when, how urgent, and the detail that makes it actionable a week later
 * — and a row of placeholders makes three of them invisible. These are the
 * shared Graphite fields, so this reads as the same form as editing a profile
 * or opening a placement.
 */
function TaskComposer({
  open,
  openCount,
  lateCount,
  scope,
  onScopeChange,
  canScopeToJob,
  onToggle,
  onAdd,
  onCancel,
  isBusy,
  isLoading,
  isError,
}: {
  open: TaskRow[]
  openCount: number
  lateCount: number
  scope: TaskScope
  onScopeChange: (scope: TaskScope) => void
  canScopeToJob: boolean
  onToggle: (task: TaskRow) => void
  onAdd: (
    title: string,
    dueDate: string,
    priority: TaskPriority,
    description?: string,
  ) => Promise<boolean>
  onCancel: () => void
  isBusy: boolean
  isLoading: boolean
  isError: boolean
}) {
  const [title, setTitle] = useState("")
  const [due, setDue] = useState("")
  const [priority, setPriority] = useState<TaskPriority>("medium")
  const [description, setDescription] = useState("")

  const ready = Boolean(title.trim()) && Boolean(due)

  /*
    `isBusy` comes back from the mutation and is therefore a frame behind the
    click, so it cannot stop the second of two fast ones — Enter on the title
    field and the button are two ways to fire the same submit. The claim is
    taken here, synchronously, before anything is awaited.
  */
  const sending = useRef(false)

  const submit = async () => {
    // `isBusy` covers a mutation started elsewhere — ticking a task done in the
    // rail — which is what disables the button; Enter has to respect it too.
    if (!ready || isBusy || sending.current) return
    sending.current = true
    try {
      // Only leave once the task is actually stored — a failed create used to
      // take the typing with it.
      const created = await onAdd(title.trim(), due, priority, description.trim())
      if (created) onCancel()
    } finally {
      sending.current = false
    }
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-4 py-4 @3xl:px-[26px] @3xl:py-5">
        <OverlayNav
          className="mb-4"
          onBack={onCancel}
          backLabel="Back to all tasks"
          title="New task"
          /* Where the rail cannot fit, the count is how the rest of them stay
             in view — the same fold 14o makes for comments. */
          meta={
            <span className={cn(META, "text-muted-foreground @xl:hidden")}>
              {openCount} open
            </span>
          }
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <Field
            label="Task"
            value={title}
            onChange={setTitle}
            onKeyDown={(event) => event.key === "Enter" && submit()}
            placeholder="What needs doing"
          />

          <div className="mt-6 grid grid-cols-1 gap-6 @xl:grid-cols-2">
            <FieldShell label="Due date">
              <input
                type="date"
                value={due}
                onChange={(event) => setDue(event.target.value)}
                aria-label="Due date"
                className={cn(FIELD_UNDERLINE, "pb-[7px] font-mono text-[12.5px]")}
              />
            </FieldShell>

            <Segmented
              label="Priority"
              options={PRIORITIES}
              value={priority}
              onChange={setPriority}
            />
          </div>

          {/*
            The description the API stores. The docked composer only opened this
            once a title had been typed, and the rebuild before that hard-coded
            an empty string into the payload — so the field the endpoint asks
            for was the one nobody could see.
          */}
          <Field
            className="mt-6 flex flex-1 flex-col pb-1"
            controlClassName="min-h-[84px] flex-1"
            label="Detail"
            value={description}
            onChange={setDescription}
            multiline
            placeholder="Anything worth keeping with it — context, a link, what “done” means"
          />
        </div>

        <div className="flex flex-none items-center gap-[22px] border-t pb-1 pt-4">
          <TextAction
            tone="teal"
            onClick={submit}
            disabled={isBusy || !ready}
            className="text-[12.5px]"
          >
            {isBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Add task
          </TextAction>
          <TextAction onClick={onCancel} disabled={isBusy} className="text-[12.5px]">
            Cancel
          </TextAction>
          {!ready && (
            <span className="ml-auto text-[11px] text-muted-foreground">
              A task needs a name and a date.
            </span>
          )}
        </div>
      </div>

      <SideRail
        label="Open tasks"
        count={
          lateCount > 0 ? (
            <>
              {openCount} · <span className="text-destructive">{lateCount} late</span>
            </>
          ) : (
            openCount
          )
        }
        header={
          canScopeToJob ? (
            <ScopeFilter
              scope={scope}
              onScopeChange={onScopeChange}
              className="mt-3.5"
            />
          ) : undefined
        }
      >
        <OpenTasks
          open={open}
          onToggle={onToggle}
          isLoading={isLoading}
          isError={isError}
          resetKey={scope}
          compact
        />
      </SideRail>
    </div>
  )
}

export function TasksView({
  mode,
  onModeChange,
  open,
  closed,
  openCount,
  lateCount,
  scope,
  onScopeChange,
  canScopeToJob,
  onToggle,
  onAdd,
  onBack,
  isBusy,
  isLoading,
  isError,
}: {
  mode: TaskMode
  onModeChange: (mode: TaskMode) => void
  open: TaskRow[]
  closed: TaskRow[]
  openCount: number
  lateCount: number
  scope: TaskScope
  onScopeChange: (scope: TaskScope) => void
  canScopeToJob: boolean
  onToggle: (task: TaskRow) => void
  onAdd: (
    title: string,
    dueDate: string,
    priority: TaskPriority,
    description?: string,
  ) => Promise<boolean>
  onBack: () => void
  isBusy: boolean
  isLoading: boolean
  isError: boolean
}) {
  /*
    The composer cannot be reached when the list itself failed to load: the rail
    beside it would be an error message, and a task written against a record
    whose tasks are unknown is a duplicate waiting to happen.
  */
  if (mode === "new" && !isError) {
    return (
      <TaskComposer
        open={open}
        openCount={openCount}
        lateCount={lateCount}
        scope={scope}
        onScopeChange={onScopeChange}
        canScopeToJob={canScopeToJob}
        onToggle={onToggle}
        onAdd={onAdd}
        onCancel={() => onModeChange("list")}
        isBusy={isBusy}
        isLoading={isLoading}
        isError={isError}
      />
    )
  }

  return (
    <TaskList
      open={open}
      closed={closed}
      openCount={openCount}
      lateCount={lateCount}
      scope={scope}
      onScopeChange={onScopeChange}
      canScopeToJob={canScopeToJob}
      onToggle={onToggle}
      onNew={() => onModeChange("new")}
      onBack={onBack}
      isLoading={isLoading}
      isError={isError}
    />
  )
}
